<template>
  <main class="marketing-page">
    <section class="marketing-shell">
      <header class="marketing-header">
        <p class="marketing-eyebrow">Marketing</p>
        <h1>Campaigns</h1>
        <p class="marketing-subtitle">
          Send email campaigns to past contacts — win-back offers, seasonal promotions, and more.
        </p>
      </header>

      <div v-if="pageError" class="banner banner-error">{{ pageError }}</div>

      <section class="panel">
        <div class="panel-header panel-header-row">
          <div>
            <h2>New Campaign</h2>
            <p>Contacts who've unsubscribed are always excluded automatically.</p>
          </div>
          <button class="new-button" type="button" @click="showComposer = !showComposer">
            <v-icon size="18">{{ showComposer ? 'mdi-close' : 'mdi-plus' }}</v-icon>
            <span>{{ showComposer ? 'Close' : 'New Campaign' }}</span>
          </button>
        </div>

        <div v-if="showComposer" class="composer">
          <label class="field">
            <span>Subject</span>
            <input class="form-field" type="text" v-model="form.subject" placeholder="A quick note from us" />
          </label>

          <label class="field">
            <span>Message</span>
            <textarea class="form-field form-textarea" v-model="form.body" rows="6" placeholder="Write your message here…"></textarea>
          </label>

          <div class="audience-builder">
            <span class="audience-label">Audience</span>

            <div class="audience-options">
              <label class="radio-option">
                <input type="radio" value="all" v-model="form.audienceType" />
                <span>All contacts</span>
              </label>

              <label class="radio-option">
                <input type="radio" value="no_recent_job" v-model="form.audienceType" />
                <span>No completed job in the last</span>
                <input
                  class="form-field form-field--inline"
                  type="number"
                  min="1"
                  v-model="form.months"
                  :disabled="form.audienceType !== 'no_recent_job'"
                />
                <span>months</span>
              </label>

              <label class="radio-option">
                <input type="radio" value="status" v-model="form.audienceType" />
                <span>Contacts with a lead status of</span>
                <select
                  class="form-field form-field--inline"
                  v-model="form.status"
                  :disabled="form.audienceType !== 'status'"
                >
                  <option v-for="(label, value) in statusLabels" :key="value" :value="value">{{ label }}</option>
                </select>
              </label>
            </div>

            <button class="preview-button" type="button" @click="previewAudience" :disabled="previewing">
              <v-progress-circular v-if="previewing" indeterminate size="14" width="2" />
              <span v-else>Preview recipients</span>
            </button>
            <span v-if="previewCount !== null" class="preview-count">{{ previewCount }} contact(s) would receive this</span>
          </div>

          <div class="form-actions">
            <button class="save-button save-button--ghost" type="button" @click="createCampaign(false)" :disabled="saving">
              Save as Draft
            </button>
            <button class="save-button" type="button" @click="createCampaign(true)" :disabled="saving">
              <v-progress-circular v-if="saving" indeterminate size="18" width="2" />
              <template v-else>Save &amp; Send Now</template>
            </button>
          </div>
        </div>
      </section>

      <section class="panel">
        <div class="panel-header">
          <h2>Campaign History</h2>
        </div>

        <div v-if="loading" class="empty-state">
          <v-progress-circular indeterminate size="24" color="primary" />
          <span>Loading…</span>
        </div>

        <table v-else-if="campaigns.length" class="campaigns-table">
          <thead>
            <tr>
              <th>Subject</th>
              <th>Status</th>
              <th>Recipients</th>
              <th>Sent</th>
              <th>Failed</th>
              <th>Created</th>
              <th class="actions-col"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="campaign in campaigns" :key="campaign.id">
              <td>{{ campaign.subject }}</td>
              <td><span class="status-pill" :class="`status-${campaign.status}`">{{ campaign.status }}</span></td>
              <td>{{ campaign.recipient_count }}</td>
              <td>{{ campaign.sent_count }}</td>
              <td>{{ campaign.failed_count }}</td>
              <td>{{ formatDate(campaign.created_at) }}</td>
              <td class="actions-col">
                <button
                  v-if="campaign.status === 'draft'"
                  class="row-action"
                  type="button"
                  :disabled="sendingId === campaign.id"
                  @click="sendNow(campaign)"
                >
                  Send Now
                </button>
                <button
                  v-if="campaign.status === 'draft' || campaign.status === 'scheduled'"
                  class="row-action row-action--danger"
                  type="button"
                  @click="deleteCampaign(campaign)"
                >
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-else class="empty-state">
          <v-icon size="28">mdi-email-multiple-outline</v-icon>
          <span>No campaigns yet — create one above.</span>
        </div>
      </section>
    </section>
  </main>
</template>

<script setup>
import { reactive, ref, watchEffect } from 'vue'
import { company, getAuthToken, API_BASE_URL } from '@/stores/appConfig'
import { logAction } from '@/utils/logAction'
import { statusLabels } from '@/constants/leads'

const campaigns = ref([])
const loading = ref(false)
const pageError = ref('')

const showComposer = ref(false)
const saving = ref(false)
const sendingId = ref(null)
const previewing = ref(false)
const previewCount = ref(null)

const form = reactive({
  subject: '',
  body: '',
  audienceType: 'all',
  months: 6,
  status: 'won',
})

function resetForm() {
  form.subject = ''
  form.body = ''
  form.audienceType = 'all'
  form.months = 6
  form.status = 'won'
  previewCount.value = null
}

function currentAudienceFilter() {
  if (form.audienceType === 'no_recent_job') {
    return { type: 'no_recent_job', months: Number(form.months) || 6 }
  }
  if (form.audienceType === 'status') {
    return { type: 'status', status: form.status }
  }
  return { type: 'all' }
}

async function authHeaders() {
  const token = await getAuthToken()
  return token ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } : { 'Content-Type': 'application/json' }
}

async function fetchCampaigns() {
  loading.value = true

  try {
    const headers = await authHeaders()
    const response = await fetch(
      `${API_BASE_URL}/marketing/campaigns?company_id=${encodeURIComponent(company.company_id)}`,
      { headers }
    )
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to load campaigns.')
    }

    campaigns.value = data.campaigns || []
  } catch (err) {
    pageError.value = err.message || 'Failed to load campaigns.'
  } finally {
    loading.value = false
  }
}

watchEffect(() => {
  if (company.company_id) fetchCampaigns()
})

async function previewAudience() {
  previewing.value = true
  previewCount.value = null

  try {
    const headers = await authHeaders()
    const response = await fetch(`${API_BASE_URL}/marketing/campaigns/preview-audience`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ company_id: company.company_id, audience_filter: currentAudienceFilter() }),
    })
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to preview audience.')
    }

    previewCount.value = data.count
  } catch (err) {
    pageError.value = err.message || 'Failed to preview audience.'
  } finally {
    previewing.value = false
  }
}

async function createCampaign(sendImmediately) {
  if (!form.subject.trim() || !form.body.trim()) {
    pageError.value = 'Subject and message are required.'
    return
  }

  pageError.value = ''
  saving.value = true

  try {
    const headers = await authHeaders()
    const response = await fetch(`${API_BASE_URL}/marketing/campaigns`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        company_id: company.company_id,
        subject: form.subject.trim(),
        body: form.body.trim(),
        audience_filter: currentAudienceFilter(),
      }),
    })
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to create campaign.')
    }

    const token = await getAuthToken()
    logAction(token, {
      action: 'marketing.campaign-create',
      label: `Created campaign "${data.campaign.subject}"`,
      company_id: company.company_id,
    })

    if (sendImmediately) {
      await sendNow(data.campaign)
    }

    resetForm()
    showComposer.value = false
    await fetchCampaigns()
  } catch (err) {
    pageError.value = err.message || 'Failed to create campaign.'
  } finally {
    saving.value = false
  }
}

async function sendNow(campaign) {
  sendingId.value = campaign.id
  pageError.value = ''

  try {
    const headers = await authHeaders()
    const response = await fetch(`${API_BASE_URL}/marketing/campaigns/${campaign.id}/send`, {
      method: 'POST',
      headers,
      body: JSON.stringify({}),
    })
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to send campaign.')
    }

    const token = await getAuthToken()
    logAction(token, {
      action: 'marketing.campaign-send',
      label: `Sent campaign "${campaign.subject}"`,
      company_id: company.company_id,
    })

    await fetchCampaigns()
  } catch (err) {
    pageError.value = err.message || 'Failed to send campaign.'
  } finally {
    sendingId.value = null
  }
}

async function deleteCampaign(campaign) {
  if (!confirm(`Delete the campaign "${campaign.subject}"?`)) return

  try {
    const headers = await authHeaders()
    const response = await fetch(`${API_BASE_URL}/marketing/campaigns/${campaign.id}`, {
      method: 'DELETE',
      headers,
    })
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to delete campaign.')
    }

    await fetchCampaigns()
  } catch (err) {
    pageError.value = err.message || 'Failed to delete campaign.'
  }
}

function formatDate(value) {
  return value ? new Date(value).toLocaleDateString('en-CA') : '—'
}
</script>

<style scoped>
.marketing-page {
  min-height: calc(100dvh - 70px);
  padding: 32px 18px 110px;
  background: var(--surface-alt);
  color: var(--text-primary);
}

.marketing-shell {
  width: min(980px, 100%);
  margin: 0 auto;
  display: grid;
  gap: 20px;
}

.marketing-eyebrow {
  margin: 0 0 6px;
  color: #1f7a63;
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.marketing-header h1 {
  margin: 0;
  font-size: 2rem;
  font-weight: 850;
  line-height: 1.1;
}

.marketing-subtitle {
  margin: 8px 0 0;
  color: var(--text-secondary);
  font-size: 0.92rem;
}

.banner {
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.86rem;
}

.banner-error {
  color: #8a2b1f;
  background: #fdecea;
  border: 1px solid #f3c6bf;
}

.panel {
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  padding: 20px;
}

.panel-header h2 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 800;
}

.panel-header p {
  margin: 4px 0 16px;
  color: var(--text-secondary);
  font-size: 0.86rem;
}

.panel-header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.new-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 14px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
  color: var(--text-primary);
  font: inherit;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
}

.composer {
  display: grid;
  gap: 14px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--border-subtle);
}

.field {
  display: grid;
  gap: 6px;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--text-secondary);
}

.form-field {
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
  color: var(--text-primary);
  font: inherit;
  font-size: 0.9rem;
  outline: none;
}

.form-textarea {
  height: auto;
  padding: 10px 12px;
  resize: vertical;
}

.form-field--inline {
  display: inline-block;
  width: auto;
  height: 32px;
  padding: 0 8px;
}

.audience-builder {
  display: grid;
  gap: 10px;
  padding: 14px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
}

.audience-label {
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--text-secondary);
}

.audience-options {
  display: grid;
  gap: 10px;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.86rem;
  color: var(--text-primary);
  font-weight: 500;
}

.preview-button {
  justify-self: start;
  height: 34px;
  padding: 0 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text-primary);
  font: inherit;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
}

.preview-count {
  font-size: 0.82rem;
  color: var(--text-secondary);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.save-button {
  min-width: 130px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 0;
  border-radius: 8px;
  color: #ffffff;
  background: #f26e2e;
  font: inherit;
  font-size: 0.86rem;
  font-weight: 800;
  cursor: pointer;
}

.save-button--ghost {
  color: var(--text-primary);
  background: var(--surface-alt);
  border: 1px solid var(--border-subtle);
}

.save-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 32px 16px;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.campaigns-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.86rem;
}

.campaigns-table th {
  text-align: left;
  padding: 8px 10px;
  color: var(--text-secondary);
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid var(--border-subtle);
}

.campaigns-table td {
  padding: 10px;
  border-bottom: 1px solid var(--border-subtle);
}

.actions-col {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.row-action {
  height: 30px;
  padding: 0 10px;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  background: var(--surface);
  color: var(--text-primary);
  font: inherit;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
}

.row-action--danger {
  color: #8a2b1f;
}

.status-pill {
  display: inline-block;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
}

.status-draft { color: var(--text-secondary); background: var(--surface-alt); }
.status-scheduled { color: #8a6d1f; background: #fff1dc; }
.status-sending { color: #29528a; background: #e8f1ff; }
.status-sent { color: #1f5c4d; background: #eef8f5; }

@media (max-width: 620px) {
  .marketing-page {
    padding: 22px 14px 110px;
  }

  .panel-header-row {
    flex-direction: column;
  }
}
</style>
